import { z } from 'zod'

/**
 * Active Dropships contracts (Screen 3). Pinned to the backend's GET /api/active
 * response and the manual-management mutation shapes — the backend is being built
 * to these exact wire formats in parallel.
 *
 * CANCELLED is a valid status even though /api/active never returns a cancelled
 * row: the cancel mutation returns the just-cancelled order in this same shape.
 */

export const activeStatusSchema = z.enum([
  'SUBMITTED',
  'TRACKING_RECEIVED',
  'SHOPIFY_UPDATED',
  'ERROR',
  'OVERDUE',
  'CANCELLED',
  'COMPLETED',
])
export type ActiveStatus = z.infer<typeof activeStatusSchema>

export const form5106StatusSchema = z.enum(['PENDING', 'ON_FILE'])
export type Form5106Status = z.infer<typeof form5106StatusSchema>

export const trackingSourceSchema = z.enum(['API', 'MANUAL'])
export type TrackingSource = z.infer<typeof trackingSourceSchema>

export const trackingSchema = z.object({
  id: z.number(),
  transporter: z.string(),
  tracking_number: z.string(),
  source: trackingSourceSchema,
  created_at: z.string(),
})
export type Tracking = z.infer<typeof trackingSchema>

export const activeOrderSchema = z.object({
  dropship_order_id: z.number(),
  shopify_order_id: z.string(),
  order_number: z.string(),
  reference_client: z.string(),
  customer_name: z.string(),
  status: activeStatusSchema,
  kh_status: z.string().nullable(),
  form_5106_status: form5106StatusSchema,
  is_vip_order: z.boolean(),
  submitted_by: z.string(),
  submitted_at: z.string(),
  shopify_updated_at: z.string().nullable(),
  internal_note: z.string().nullable(),
  error_message: z.string().nullable(),
  trackings: z.array(trackingSchema),
})
export type ActiveOrder = z.infer<typeof activeOrderSchema>

export const activeResponseSchema = z.object({
  orders: z.array(activeOrderSchema),
  generated_at: z.string(),
})
export type ActiveResponse = z.infer<typeof activeResponseSchema>

// ---- GET /api/dropship/{id} — persisted submission detail ----

export const dropshipDetailItemSchema = z.object({
  shopify_line_item_id: z.string(),
  product_title: z.string(),
  variant_title: z.string().nullable(),
  sku: z.string(),
  ean: z.string().nullable(),
  quantity: z.number(),
  retail_price: z.string(),
  line_total: z.string(),
})
export type DropshipDetailItem = z.infer<typeof dropshipDetailItemSchema>

export const dropshipDetailShippingSchema = z.object({
  name: z.string(),
  address1: z.string(),
  address2: z.string(),
  city: z.string(),
  province_code: z.string(),
  zip: z.string(),
  country_code: z.string(),
})
export type DropshipDetailShipping = z.infer<typeof dropshipDetailShippingSchema>

export const dropshipOverrideDetailSchema = z.object({
  override_by: z.string(),
  override_at: z.string().nullable(),
  override_note: z.string(),
  selected_total: z.string(),
  customer_order_count: z.number(),
  customer_aov: z.string(),
})
export type DropshipOverrideDetail = z.infer<typeof dropshipOverrideDetailSchema>

export const dropshipDetailSchema = z.object({
  dropship_order_id: z.number(),
  shopify_order_id: z.string(),
  order_number: z.string(),
  reference_client: z.string(),
  kh_order_id: z.string().nullable(),
  kh_status: z.string().nullable(),
  status: activeStatusSchema,
  form_5106_status: form5106StatusSchema,
  is_vip_order: z.boolean(),
  customer_name: z.string(),
  submitted_by: z.string(),
  submitted_at: z.string().nullable(),
  internal_note: z.string().nullable(),
  dropship_fee: z.string(),
  selected_total: z.string(),
  shipping_address: dropshipDetailShippingSchema,
  items: z.array(dropshipDetailItemSchema),
  override: dropshipOverrideDetailSchema.nullable(),
  trackings: z.array(trackingSchema),
})
export type DropshipDetail = z.infer<typeof dropshipDetailSchema>

// ---- POST /api/dropship/refresh-tracking ----

export const refreshSummarySchema = z.object({
  checked: z.number(),
  tracking_received: z.number(),
  shopify_updated: z.number(),
  overdue_flagged: z.number(),
  errors: z.number(),
})
export type RefreshSummary = z.infer<typeof refreshSummarySchema>
